import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { format, parseISO, subDays, eachDayOfInterval } from "date-fns";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppHeader } from "@/components/app/AppHeader";
import { KpiRow } from "@/components/app/KpiRow";
import {
  useAllProfiles,
  useAllSubmissions,
} from "@/hooks/use-admin-data";
import { DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";

export const Route = createFileRoute("/_app/_admin/admin/overview")({
  component: () => <Navigate to="/admin/settings" search={{ tab: "overview" } as never} />,
});

export function AdminOverview() {
  const { data: profiles = [] } = useAllProfiles();
  const { data: submissions = [] } = useAllSubmissions();

  const now = new Date();
  const monthSubs = submissions.filter((s) => {
    const d = parseISO(s.submission_date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const activeUsers = profiles.filter((p) => p.status === "active").length;
  const corrections = monthSubs.filter((s) => s.status === "correction_needed").length;
  const reviewed = monthSubs.filter((s) => s.status !== "pending").length;
  const approved = monthSubs.filter((s) => s.status === "approved").length;
  const approvalRate = reviewed ? Math.round((approved / reviewed) * 100) : 0;
  const totalCalls = monthSubs.filter((s) => s.department === "calling").length;

  // Per-department aggregation
  const depts = Object.keys(DEPARTMENT_CONFIGS) as Department[];
  const deptAgg = depts.map((d) => {
    const subs = monthSubs.filter((s) => s.department === d);
    const people = profiles.filter((p) => p.department === d).length;
    return {
      dept: d,
      label: DEPARTMENT_LABELS[d],
      count: subs.length,
      points: subs.reduce((s, r) => s + r.points, 0),
      people,
    };
  });

  // Leaderboard top 5 by points this month
  const userTotals = new Map<string, number>();
  for (const s of monthSubs) {
    userTotals.set(s.user_id, (userTotals.get(s.user_id) ?? 0) + s.points);
  }
  const leaderboard = profiles
    .filter((p) => p.department === "editor")
    .map((p) => ({ profile: p, points: userTotals.get(p.id) ?? 0 }))
    .sort((a, b) => b.points - a.points)
    .slice(0, 5);

  // 30-day company trend
  const start = subDays(now, 29);
  const days = eachDayOfInterval({ start, end: now });
  const dayMap = new Map<string, number>();
  for (const s of submissions) {
    dayMap.set(s.submission_date, (dayMap.get(s.submission_date) ?? 0) + 1);
  }
  const trend = days.map((d) => ({
    date: format(d, "dd MMM"),
    count: dayMap.get(format(d, "yyyy-MM-dd")) ?? 0,
  }));

  // Activity feed
  const recent = submissions.slice(0, 8);
  const profileById = new Map(profiles.map((p) => [p.id, p]));

  return (
    <>
      <AppHeader subtitle="Company-wide performance" />
      <KpiRow
        items={[
          { label: "Active Users", value: String(activeUsers), variant: "primary" },
          {
            label: "Submissions This Month",
            value: String(monthSubs.length),
            trend: `${approved} approved`,
          },
          {
            label: "Open Corrections",
            value: String(corrections),
            variant: "danger",
            trend: "needs review",
          },
          {
            label: "Avg Approval",
            value: reviewed ? `${approvalRate}%` : "—",
            trend: `${reviewed} reviewed`,
          },
          { label: "Total Calls", value: String(totalCalls), trend: "this month" },
        ]}
      />

      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Company Output
          </div>
          <h2 className="text-[18px] font-bold">30-Day Trend</h2>
          <div className="mt-4 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="adm-out" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 11,
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                    background: "var(--card)",
                    color: "var(--foreground)",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  fill="url(#adm-out)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Top Performers
          </div>
          <h2 className="text-[18px] font-bold">Leaderboard</h2>
          <div className="mt-4 flex flex-col gap-2">
            {leaderboard.length === 0 && (
              <p className="text-[12px] text-muted-foreground">No activity yet.</p>
            )}
            {leaderboard.map((row, i) => (
              <div
                key={row.profile.id}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-semibold">
                      {row.profile.full_name}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {DEPARTMENT_LABELS[row.profile.department]}
                    </div>
                  </div>
                </div>
                <span className="text-[12px] font-bold">{row.points} pts</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            By Department
          </div>
          <h2 className="text-[18px] font-bold">This Month</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {deptAgg.map((d) => (
              <Link
                key={d.dept}
                to="/admin/departments"
                search={{ dept: d.dept } as never}
                className="rounded-lg border border-border p-3 transition-colors hover:bg-muted/50"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold">{d.label}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {d.people} {d.people === 1 ? "person" : "people"}
                  </div>
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-[20px] font-bold">{d.count}</span>
                  <span className="text-[10px] text-muted-foreground">submissions</span>
                </div>
                {d.dept === "editor" && (
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {d.points} pts
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Activity
          </div>
          <h2 className="text-[18px] font-bold">Recent submissions</h2>
          <div className="mt-3 flex flex-col gap-2">
            {recent.length === 0 && (
              <p className="text-[12px] text-muted-foreground">Nothing yet.</p>
            )}
            {recent.map((s) => {
              const p = profileById.get(s.user_id);
              return (
                <div
                  key={s.id}
                  className="flex items-center justify-between border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-medium">{s.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {p?.full_name ?? "—"} ·{" "}
                      {format(parseISO(s.submission_date), "EEE, dd MMM")}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] capitalize">
                    {s.status.replace("_", " ")}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
